import React, { useRef, useState } from 'react';
import { TextInput, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '../design-system';
import { InputProps } from '../design-system/Input';
import { colors } from '../../theme';
import { VariableGroup } from './flow-types';
import { VariablePicker } from './VariablePicker';

interface InterpolatableInputProps extends Omit<InputProps, 'rightIcon'> {
  variables: VariableGroup[];
}

/** A single-line Input with an "insert value" button that opens the variable picker and
 * inserts {{path}} at the current cursor position — for the flow builder's template fields
 * (valueTemplate, event dates, confirmation messages, etc). Kept as its own component rather
 * than extending the shared Input (used app-wide by fields that never need cursor tracking)
 * so this cursor/selection plumbing stays scoped to the flow builder. */
export const InterpolatableInput: React.FC<InterpolatableInputProps> = ({ variables, value, onChangeText, ...inputProps }) => {
  const inputRef = useRef<TextInput>(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [pickerVisible, setPickerVisible] = useState(false);
  const text = String(value ?? '');

  const insertAtCursor = (path: string) => {
    const insertText = `{{${path}}}`;
    const { start, end } = selection;
    const before = text.slice(0, start);
    const after = text.slice(end);
    const next = `${before}${insertText}${after}`;
    onChangeText?.(next);
    const cursor = start + insertText.length;
    requestAnimationFrame(() => {
      inputRef.current?.setNativeProps({ selection: { start: cursor, end: cursor } });
    });
  };

  return (
    <>
      <Input
        {...inputProps}
        ref={inputRef}
        value={value}
        onChangeText={onChangeText}
        onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
        rightIcon={
          <TouchableOpacity onPress={() => setPickerVisible(true)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name="code-slash-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
        }
      />
      <VariablePicker visible={pickerVisible} onClose={() => setPickerVisible(false)} groups={variables} onSelect={insertAtCursor} />
    </>
  );
};
